import { ApiError, DiscordApi, DiscordInfoResponse, Token } from "@devour/client";
import getConfig from "@/utils/getConfig";
import { useMutation, useQuery } from "@tanstack/react-query";
import { addError } from "@/redux/meta/metaActions";
import { useDispatch } from "react-redux";

/**
 * Handles Discord authentication using a popup window instead of full page redirect.
 * @param fullToken - The authentication token.
 * @param onSuccess - Optional callback when authentication succeeds
 * @param onError - Optional callback when authentication fails
 */
export async function handleDiscordAuthenticate(
    fullToken: Token,
    onSuccess?: () => void,
    onError?: (error: string) => void,
    returnUrl?: string, // Optional: specify where to return after auth
): Promise<void> {
    try {
        // Use the current page as return URL if not specified
        const currentReturnUrl = returnUrl || window.location.pathname;

        /*
         * Fetch the Discord OAuth2 URL using the generated client
         * We need to manually call the API with returnUrl parameter since the generated client doesn't support it
         */
        const config = getConfig(fullToken);
        const response = await fetch(`${config.basePath}/link-discord?returnUrl=${encodeURIComponent(currentReturnUrl)}`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${fullToken.token}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data && data.url) {
            // Open Discord auth in popup window
            const popup = window.open(
                data.url,
                "discord-auth",
                "width=500,height=700,scrollbars=yes,resizable=yes,status=yes,location=yes,toolbar=no,menubar=no",
            );

            if (!popup) {
                throw new Error("Popup blocked. Please allow popups for Discord authentication.");
            }

            // Listen for postMessage from popup when auth completes
            const handleMessage = (event: MessageEvent) => {
                // Verify origin for security
                if (event.origin !== window.location.origin) {
                    return;
                }

                if (event.data.type === "DISCORD_AUTH_SUCCESS") {
                    clearInterval(pollPopup);
                    popup.close();
                    window.removeEventListener("message", handleMessage);
                    onSuccess?.();
                } else if (event.data.type === "DISCORD_AUTH_ERROR") {
                    clearInterval(pollPopup);
                    popup.close();
                    window.removeEventListener("message", handleMessage);
                    onError?.(event.data.error || "Authentication failed");
                }
            };

            window.addEventListener("message", handleMessage);

            // Poll the popup to detect when auth is complete (fallback)
            const pollPopup = setInterval(() => {
                try {
                    // Check if popup was closed manually
                    if (popup.closed) {
                        clearInterval(pollPopup);
                        window.removeEventListener("message", handleMessage);
                        onError?.("Authentication was cancelled");
                        return;
                    }

                    // Fallback: Try to detect URL change (may not work due to CORS)
                    try {
                        if (popup.location.href.includes(window.location.origin) &&
                            (popup.location.href.includes("/account") || popup.location.href.includes("discord-success"))) {
                            clearInterval(pollPopup);
                            popup.close();
                            window.removeEventListener("message", handleMessage);

                            setTimeout(() => {
                                onSuccess?.();
                            }, 1000);
                        }
                    } catch (corsError) {
                        // Expected - cross-origin restriction
                    }
                } catch (error) {
                    // Cross-origin errors are expected during OAuth flow
                }
            }, 1000);

            // Timeout after 5 minutes
            setTimeout(() => {
                if (!popup.closed) {
                    clearInterval(pollPopup);
                    popup.close();
                    onError?.("Authentication timed out");
                }
            }, 300000);
        } else {
            throw new Error("Could not get Discord authentication URL");
        }
    } catch (error) {
        console.error("Discord authentication error:", error);
        onError?.(error instanceof Error ? error.message : "Authentication failed");
    }
}

async function fetchDiscordAccount(): Promise<DiscordInfoResponse> {
    return await new DiscordApi(getConfig()).getDiscordAccount();
}

function fetchDiscordAccountQuery() {
    return {
        queryKey: [
            "get-discord-account",
        ],
        queryFn: fetchDiscordAccount,
        keepPreviousData: true,
        staleTime: 30 * 60000,
        refetchOnWindowFocus: false,
    } as const;
}

export function useGetDiscordAccount(): { data: DiscordInfoResponse, refetch: () => void, isLoading: boolean } {
    return useQuery(fetchDiscordAccountQuery());
}

export function useDisconnectDiscordAccount(fullToken: Token) {
    const dispatch = useDispatch();

    return useMutation({
        mutationFn: async () => {
            await new DiscordApi(getConfig(fullToken)).disconnectDiscordAccount();
            window.location.reload();
        },
        onError: async (error) => dispatch(await addError(error as ApiError)),
    });
}